import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';

// ─── Auto-restart on crash ────────────────────────────────────────────────
// TVs are typically wall-mounted and unattended. If the app throws an
// uncaught error or the renderer hangs, we want to recover automatically
// rather than leave a black screen until someone notices. Two layers:
//
//   1. ErrorBoundary in App.jsx catches React render errors and reloads.
//   2. Window-level handlers below catch everything else (network errors
//      that bubble up unhandled, ResizeObserver loops, async exceptions
//      from setInterval handlers that don't await, etc.) and reload after
//      a short delay so we don't reload-loop on a persistent failure.
//
// The 10s delay also gives the network a chance to come back if the TV
// briefly lost WiFi — we don't want to thrash the backend on every blip.

const RELOAD_DELAY_MS = 10_000;
let reloadScheduled = false;

function scheduleReload(reason) {
  if (reloadScheduled) return;
  reloadScheduled = true;
  // eslint-disable-next-line no-console
  console.error('[player] scheduling reload in', RELOAD_DELAY_MS, 'ms —', reason);
  setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
}

window.addEventListener('error', (e) => {
  scheduleReload(`error: ${e.message}`);
});
window.addEventListener('unhandledrejection', (e) => {
  scheduleReload(`unhandled rejection: ${e.reason?.message || e.reason}`);
});

// ─── Service worker (PWA + offline cache) ─────────────────────────────────
// Registered after first paint so the boot screen renders without competing
// for the main thread. The SW caches the app shell and the most recent
// playback response so the player keeps showing content if WiFi drops.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[player] sw register failed:', err);
    });
  });
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
